/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, MouseEvent, ReactNode } from 'react';
import { BaseReactProps } from '../../../types';

export enum PickerVariant {
  BASIC = 'BASIC',
  DROPDOWN = 'DROPDOWN',
}

export type PickerVariantKey = keyof typeof PickerVariant;

export interface ICalendarContext {
  focusedDate?: Date;
  selectionType?: SelectionType;
  currentRange?: any[];
  selectedRange?: any[];
}

export const DATEFORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  small: { year: '2-digit', month: 'numeric', day: 'numeric' },
  medium: { year: 'numeric', month: '2-digit', day: '2-digit' },
  large: { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' },
};

export type DatePickerInputValue = string | Date | Date[] | string[];
export type RangeDatePickerInputValue = Date[] | string[];
export type SingeDatePickerInputValue = string | Date;
export type WEEKDAYFORMAT = 'long' | 'short' | 'narrow' | undefined;
export type FormatterStyle = 'small' | 'medium' | 'large';
export type SelectionType = 'single' | 'range';
export type VariantType = 'single' | 'double';
export type IconPosition = 'left' | 'right';

export interface DatePickerInputProps extends BaseReactProps {
  pickerVariant?: PickerVariantKey;
  id?: string;
  value?: DatePickerInputValue;
  maxValue?: Date;
  minValue?: Date;
  formatStyle?: FormatterStyle;
  onChange?: (value?: Date | Date[]) => void;
  placeholder?: string;
  label?: string;
  name: string;
  locale?: string;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onFocus?: (event: DatePickerInputValue | undefined) => void;
  onBlur?: (event: DatePickerInputValue | undefined) => void;
  selectionType?: SelectionType;
  variant?: VariantType;
  error?: ReactNode;
  dropdown?: boolean;
  modalClassName?: string;
}

export interface DatePickerInputModalProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  locale?: string;
  maxValue?: Date;
  minValue?: Date;
  value?: DatePickerInputValue;
  isOpen: boolean;
  selectionType?: SelectionType;
  variant?: VariantType;
  onChange?: (value: any) => void;
  onClose: () => void;
}

export interface DatePickerInputCalendarProps {
  id?: string;
  value?: DatePickerInputValue;
  selectionType?: SelectionType;
  minValue?: Date;
  maxValue?: Date;
  onChange?: (value: any) => void;
  className?: string;
  style?: CSSProperties;
  locale?: string;
  variant?: VariantType;
}

export interface SingleCalendarProps {
  id?: string;
  value?: SingeDatePickerInputValue;
  minValue?: Date;
  maxValue?: Date;
  onChange?: (value: any) => void;
  locale?: string;
  className?: string;
  style?: CSSProperties;
  selectedRange?: RangeDatePickerInputValue;
  selectionType?: SelectionType;
}

export interface DoubleCalendarProps {
  id?: string;
  value?: SingeDatePickerInputValue;
  maxValue?: Date;
  minValue?: Date;
  locale?: string;
  selectionType?: SelectionType;
  selectedRange?: RangeDatePickerInputValue;
  onChange?: (value: any) => void;
  className?: string;
  style?: CSSProperties;
}

export interface CalendarMonthProps {
  firstOfMonth: Date;
  value?: SingeDatePickerInputValue;
  minValue?: Date;
  maxValue?: Date;
  onChange?: (value: any) => void;
}

export interface CalendarWeekProps {
  firstOfMonth: Date;
  lastOfMonth: Date;
  minValue?: Date;
  maxValue?: Date;
  startDate: Date;
  endDate: Date;
  value?: SingeDatePickerInputValue;
  onChange: (value: any) => void;
}

export interface CalendarDayProps {
  date: Date;
  firstOfMonth: Date;
  minValue?: Date;
  maxValue?: Date;
  isSelected: boolean;
  isWithinRange: boolean;
  isFirstDayOfWeek: boolean;
  isLastDatOfWeek: boolean;
  onChange: (value: any) => void;
}

export interface CalendarRangeHighlightProps {
  isVisible?: boolean;
  isFirstInRange?: boolean;
  isLastInRange?: boolean;
  isFirstDayOfWeek?: boolean;
  isLastDayOfWeek?: boolean;
  isToday?: boolean;
}

export interface CalendarHeaderProps {
  monthLabelId?: string;
  currentYear?: number;
  formattedMonth?: string;
  disablePrevMonth?: boolean;
  disableNextMonth?: boolean;
  locale?: string;
  onYearChange?: (year: number) => void;
  onMonthChange?: (month: number) => void;
  onNextMonthClick?: () => void;
  onPrevMonthClick?: () => void;
}
